import { useLazyQuery } from '@apollo/client';
import { LIST_TYPES } from 'constants/listTypes';
import { addNegativeToast } from 'modules/toastContainer';
import { forwardRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import noop from 'utils/noop';
import { SORT_DIRECTIONS } from 'constants/sortDirections';
import { LIST_MEMBERSHIP_SINGLE } from 'utils/fieldConstants';
import { QUERY_TYPES as q } from './SingleSelectDropdown.constants';
import { StyledCoreDropdown, StyledTopTooltip } from './SingleSelectDropdown.styled';
import {
  getFieldQueryType,
  getGQLQuery,
  getHasPagination,
  getResultsFromData,
  getSingleGQLQuery,
} from './SingleSelectDropdown.utils';

export const SINGLE_SELECT_TEST_ID = 'single-select-test-id';

// export interface SingleSelectDropdownProps extends BaseFieldProps {
//   /** Name of the class - used to override styles */
//   className?: string;
//   /** Options to default to if gql query fails */
//   defaultOptions?: any[];
//   /** The field used in this dropdown */
//   field: $Values<SINGLE_SELECT_FIELDS>;
//   disabled?: boolean;
//   isReadOnly?: boolean;
// }

const SingleSelectDropdown = forwardRef(function SingleSelectDropdown(
  {
    className,
    defaultOptions = [],
    disabled = false,
    error,
    field,
    type,
    fieldIdentifier = [],
    onChange = noop,
    placeholder = 'value',
    value,
    isReadOnly = false,
  } /*: SingleSelectDropdownProps */,
  ref,
) {
  const dispatch = useDispatch();
  const [options, setOptions] = useState(defaultOptions);
  // used only if paginated
  const [selectedOption, setSelectedOption] = useState({});
  const [hasError, setHasError] = useState(false);
  const [isPaginated, setIsPaginated] = useState(false);

  const getVariables = () =>
    getFieldQueryType(field) === q.LISTS
      ? {
          /** For Journey yes/no split tile, we want to filter out the dynamic lists for the User List option since this will not work and will always default to the exclusion path. */

          ...(type === 'JourneyStudio' &&
            field === LIST_MEMBERSHIP_SINGLE && {
              filterInfo: {
                listType: [LIST_TYPES.STANDARD, LIST_TYPES.SUPPRESSION, LIST_TYPES.INTERNAL],
              },
            }),
          search: '',
          sort: {
            sortBy: 'UpdatedAt',
            sortDirection: SORT_DIRECTIONS.DESCENDING,
          },
        }
      : {};
  const [tooltipContent, setTooltipContent] = useState('');

  const emailListSearch = (items, searchValue) => {
    return items.filter((item) => {
      const { name, id } = item;
      const displayValue = `${name}${id}` || '';
      const searchString = displayValue?.toLowerCase?.();
      const searchInput = searchValue?.toLowerCase?.();
      return searchString?.indexOf?.(searchInput) !== -1;
    });
  };

  const queryArgs = {
    fetchPolicy: 'cache-and-network',
    variables: getVariables(),
    onError: () => setHasError(true),
    onCompleted: (data) => {
      const results = getResultsFromData(data, field);
      let opts = results.map((r) => ({
        ...r,
        text: r.name,
        description: r.id,
        key: r.id,
        value: `${r.id}`,
      }));
      // if paginated get selected option and put it in dropdown
      if (getHasPagination(field)) {
        // filter out duplicate, if selected option is in first page
        opts = opts.filter(({ key }) => key !== selectedOption.key);

        const isEmptySelectedOption = Object.keys(selectedOption).length === 0;

        if (!isEmptySelectedOption) {
          opts = [selectedOption, ...opts];
        }
      }
      setOptions(opts);
      setHasError(false);
    },
  };

  const getSingleQueryArgs = (queryType) => ({
    fetchPolicy: 'cache-and-network',
    variables: { id: parseInt(value, 10) },
    onError: () => setHasError(true),
    onCompleted: (data) => {
      const selectedItem = data && Object.values(data)[0];
      if (selectedItem) {
        setSelectedOption({
          text: selectedItem.name,
          key: selectedItem.id,
          value: `${selectedItem.id}`,
        });
        setHasError(false);
      } else {
        if (queryType === q.LISTS) {
          dispatch(
            addNegativeToast(
              'We’re having trouble loading one of the lists in this tile. Sorry about that!',
            ),
          );
        }
        setHasError(true);
      }
    },
  });

  const queries = {
    [q.CAMPAIGN_LABELS]: useLazyQuery(getGQLQuery(q.CAMPAIGN_LABELS), queryArgs),
    [q.LISTS]: useLazyQuery(getGQLQuery(q.LISTS), queryArgs),
    [q.MESSAGE_TYPES]: useLazyQuery(getGQLQuery(q.MESSAGE_TYPES), queryArgs),
    [q.MESSAGE_CHANNELS]: useLazyQuery(getGQLQuery(q.MESSAGE_CHANNELS), queryArgs),
  };

  const singleQueries = {
    [q.LISTS]: useLazyQuery(getSingleGQLQuery(q.LISTS), getSingleQueryArgs(q.LISTS)),
  };

  const getQuery = () => queries[getFieldQueryType(field)];
  const getQueryFunction = () => getQuery()?.[0] || noop;
  const getQueryInfo = () => getQuery()?.[1] || {};
  const getIsLoading = () => getQueryInfo().loading;

  const getSingleQuery = () => singleQueries[getFieldQueryType(field)];
  const getSingleQueryFunction = () => getSingleQuery()?.[0] || noop;

  const runQuery = (opts) => {
    const query = getQueryFunction();
    if (getHasPagination(field) && !opts) {
      const singleQuery = getSingleQueryFunction();
      singleQuery();
    }
    if (query) {
      query(opts);
    }
  };

  /**
   * If the query is paginated, there *may* be a limit on how many are loaded by
   * default, so we pull the list with the new search queries
   *
   * TODO: [WF-1129] Check this with many lists, and add debounce if there are performance issues
   */
  const handleSearchChange = (e, { searchQuery }) =>
    runQuery({ variables: { search: searchQuery } });

  useEffect(() => {
    runQuery();
    setIsPaginated(getHasPagination(field));
  }, [field]);

  /** Sometimes the single query for `selectedOption` runs/finishes after the list query; when that happens the
   * `selectedOption` value in the `options` array is not set. This checks and updates the `selectedOption` value for that case.
   * */
  useEffect(() => {
    const hasPagination = getHasPagination(field);
    /** If not paginated the `selectedOption` should already be in `options` */
    if (!hasPagination) return;

    const selectedOptionExists = options.find((option) => option?.value === selectedOption?.value);
    const isEmptySelectedOption = Object.keys(selectedOption).length === 0;

    if (options.length && !selectedOptionExists && !isEmptySelectedOption) {
      setOptions([selectedOption, ...options]);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (options.length && value && isReadOnly) {
      setTooltipContent(options?.find((o) => o.value === value)?.text);
    }
  }, [value, options, isReadOnly]);

  return (
    <StyledTopTooltip isEnabled={isReadOnly && tooltipContent} tooltipContent={tooltipContent}>
      <StyledCoreDropdown
        className={className}
        data-test={SINGLE_SELECT_TEST_ID}
        disabled={disabled}
        fluid
        isError={hasError || error}
        isLoading={getIsLoading()}
        onChange={(e, { value: updatedValue }) => onChange(updatedValue, ...fieldIdentifier)}
        options={options}
        placeholder={placeholder}
        ref={ref}
        search={field === 'emailListIds' ? emailListSearch : true}
        selection
        value={value}
        onSearchChange={isPaginated ? handleSearchChange : null}
      />
    </StyledTopTooltip>
  );
});

export default SingleSelectDropdown;
